import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BedDouble, MapPin, ArrowUpRight, Sparkles } from 'lucide-react';
import { propertiesAPI } from '../api';

/**
 * PropertiesShowcase — the homepage "Flats & houses to let" strip.
 *
 * Lives between UpcomingEventsStrip and CategoryTiles.
 * Pulls live rental listings via /api/properties/latest and renders them as
 * cards (similar pattern to UpcomingEventsStrip). The final tile is a
 * Paisley Murals callout that links to paisleymurals.co.uk (same window).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMAGE = '/property-placeholder.jpg';
// Shown when there are no active rentals at all (e.g. all expired).
const NO_PROPERTIES_IMAGE = 'https://customer-assets.emergentagent.com/job_5c7ee48a-ca63-4704-82af-18d72800934b/artifacts/4q727na5_no-flat-images.png';

const fmtPrice = (n) => (typeof n === 'number' && n > 0 ? `£${n.toLocaleString()} pcm` : 'Price on request');
const resolveImg = (url) => {
  if (!url) return `${BACKEND_URL}${FALLBACK_IMAGE}`;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const PropertiesShowcase = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    propertiesAPI
      .latest(6)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Hide the whole section if there's nothing to show
  if (!loading && items.length === 0) {
    return (
      <section className="relative py-24" data-testid="properties-showcase-empty">
        <div className="aurora-orb w-[420px] h-[420px] top-10 left-1/3 bg-emerald-200/30" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-600 mb-3">More to explore</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-[-0.04em] text-slate-900 leading-none mb-6">
            Flats &amp;{' '}
            <span className="font-serif-accent italic font-light bg-gradient-to-r from-emerald-500 to-cyan-600 bg-clip-text text-transparent">
              houses
            </span>{' '}
            to let
          </h2>
          <img
            src={NO_PROPERTIES_IMAGE}
            alt="Property image coming soon — no rentals listed at the moment"
            className="mx-auto max-w-md w-full rounded-3xl shadow-xl shadow-emerald-500/10 ring-1 ring-slate-200/50"
            loading="lazy"
          />
          <p className="mt-6 text-slate-600 max-w-md mx-auto">
            No live listings right now. Check back soon — we update from Rightmove, Zoopla &amp; local agents most days.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24" data-testid="properties-showcase">
      <div className="aurora-orb w-[520px] h-[520px] top-10 left-1/4 bg-emerald-200/40" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Heading row */}
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-600 mb-3">More to explore</p>
            <h2 className="text-5xl md:text-6xl font-bold tracking-[-0.04em] text-slate-900 leading-none">
              Flats &amp;{' '}
              <span className="font-serif-accent italic font-light bg-gradient-to-r from-emerald-500 to-cyan-600 bg-clip-text text-transparent">
                houses
              </span>{' '}
              to let
            </h2>
            <p className="mt-4 text-base text-slate-600 max-w-2xl">
              Live rental listings — we search Rightmove, Zoopla &amp; every Paisley agent so you don't have to.
            </p>
          </div>
          <Link
            to="/properties"
            className="hidden sm:inline-flex items-center gap-2 glass-strong glass-hover rounded-full px-5 py-2.5 text-sm font-semibold text-slate-800"
            data-testid="properties-showcase-all-link"
          >
            All rentals <ArrowUpRight size={15} />
          </Link>
        </div>

        {/* Cards grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => <div key={i} className="h-72 glass rounded-3xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((p) => (
              <Link
                key={p.id}
                to={`/properties/${p.slug}`}
                className="group glass rounded-3xl overflow-hidden glass-hover relative"
                data-testid={`home-property-card-${p.slug}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={resolveImg(p.image_url)}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms]"
                    onError={(e) => {
                      if (!e.target.dataset.fb) {
                        e.target.dataset.fb = '1';
                        e.target.src = `${BACKEND_URL}${FALLBACK_IMAGE}`;
                      }
                    }}
                  />
                  <span className="absolute top-3 left-3 glass-strong rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-700">
                    {p.property_type}
                  </span>
                  {p.featured && (
                    <span className="absolute top-3 right-3 bg-fuchsia-500 text-white rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                      <Sparkles size={11} /> Featured
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <p className="text-xl font-extrabold tracking-tight text-slate-900">{fmtPrice(p.monthly_rent)}</p>
                    <ArrowUpRight size={15} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 leading-snug line-clamp-2 group-hover:text-emerald-700">
                    {p.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-xs text-slate-500">
                    {typeof p.bedrooms === 'number' && p.bedrooms > 0 && (
                      <span className="flex items-center gap-1"><BedDouble size={11} /> {p.bedrooms} bed{p.bedrooms === 1 ? '' : 's'}</span>
                    )}
                    {p.area && (
                      <span className="flex items-center gap-1 truncate"><MapPin size={11} /><span className="truncate">{p.area}</span></span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="sm:hidden mt-8 text-center">
          <Link to="/properties" className="font-semibold text-emerald-600" data-testid="properties-showcase-mobile-link">
            All rentals →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PropertiesShowcase;
